import type {
    Paysafe,
    PaysafeOptions,
    PaysafeTokenizationErrorResponse,
    PaysafeTokenResponse,
    TokenizationOptions,
} from '../models/paysafe'
import { alpha3to2 } from './alpha3to2'
import { convertToLowestDenomination } from './convertToLowestUnit'
import { CheckoutEvent, dispatchEvent, postEvent } from './events'
import type { CheckoutHandler } from './sdk'
import { v7 as uuidv7 } from 'uuid'

declare let paysafe: Paysafe

export const initialisePaysafe = async (
    checkoutInstance: CheckoutHandler,
): Promise<void> => {
    const options: PaysafeOptions = {
        currencyCode: 'USD',
        environment: 'TEST',
        accounts: {
            default: Number(
                checkoutInstance.initCheckoutResponseData.credentials
                    .paysafeAccountNumber,
            ),
        },
        fields: {
            cardNumber: {
                selector: '#gc-dg-cardNumber',
                placeholder: 'Card Number',
                errorMessage: 'Please enter a valid card number',
            },
            expiryDate: {
                selector: '#gc-dg-expiryDate',
                placeholder: 'Expiry date',
                errorMessage: 'Please enter a valid expiry date',
            },
            cvv: {
                selector: '#gc-dg-cvv',
                placeholder: 'CVV',
                errorMessage: 'Please enter a valid security code',
            },
        },
    }

    try {
        checkoutInstance.paysafeInstance = await paysafe.fields.setup(
            checkoutInstance.paysafeIframeToken,
            options,
        )
    } catch (error) {
        console.error('Error initialising paysafe by calling setup')
        console.error(JSON.stringify(error))

        const eventDescription =
            'Failed to initialise paysafe for card payments'
        dispatchEvent(CheckoutEvent.FAILURE, {
            status: 'error',
            eventDescription,
        })

        postEvent(checkoutInstance.authToken, {
            eventName: CheckoutEvent.FAILURE,
            transactionId: '',
            source: 'checkout_sdk',
            additionalProps: {
                status: 'error',
                eventDescription,
            },
            checkoutSourceRef:
                checkoutInstance.basket.channelSourceData.checkoutSourceRef,
        })
    }
}

export const generatePaysafeToken = async (
    checkoutInstance: CheckoutHandler,
): Promise<PaysafeTokenResponse> => {
    const alpha2Code = alpha3to2[checkoutInstance.customer.address.countryCode]

    if (!alpha2Code) {
        throw new Error(
            `Country code ${checkoutInstance.customer.address.countryCode} is not supported`,
        )
    }
    const tokenizationOptions: TokenizationOptions = {
        amount: convertToLowestDenomination(checkoutInstance.basketTotal),
        transactionType: 'PAYMENT',
        paymentType: 'CARD',
        // merchantRefNum must be random but also unique per call
        // otherwise it will return a 409 conflict.
        // This is also a throwaway value as we link up
        // the payment profile later on in the journey.
        merchantRefNum: uuidv7(),
        customerDetails: {
            billingDetails: {
                country: alpha2Code,
                zip: checkoutInstance.customer.address.postalCode,
                street: checkoutInstance.customer.address.street,
                city: checkoutInstance.customer.address.city,
                state: checkoutInstance.customer.address.stateCode,
            },
        },
    }

    const tokenResponse: PaysafeTokenResponse = {}

    if (!checkoutInstance.paysafeInstance) {
        throw new Error('Paysafe instance is not initialized')
    }

    try {
        tokenResponse.data = (await checkoutInstance.paysafeInstance.tokenize(
            tokenizationOptions,
        )) as PaysafeTokenResponse['data']
        return tokenResponse
    } catch (error) {
        const eventDescription = 'Failed to tokenize card for card payments'
        console.error(
            JSON.stringify({
                message: eventDescription,
                error: error,
            }),
        )
        return {
            ...tokenResponse,
            fieldErrors: (error as Partial<PaysafeTokenizationErrorResponse>)
                .fieldErrors,
        }
    }
}
